import { z } from 'zod';
import { EvaluationEfficiencyTotalsSchema, EvaluationSuiteEfficiencySchema, } from './evaluationEfficiency.js';
import { EvaluationBatchScoreSchema, EvaluationCheckNamesSchema, EvaluationCheckResultSchema, EvaluationSuiteScoreSchema, } from './evaluationScore.js';
import { AgentModelSchema } from './model.js';
import { AgentThinkingLevelSchema } from './session/run.js';
export const EvaluationTrialMetricsSchema = z.object({
    actualResponseModels: z.array(z.string()),
    /** All input tokens at the base input rate plus output tokens, so cache hit rate cannot move it. */
    cacheIndependentCostInUsd: z.number().optional(),
    cacheReadTokens: z.number(),
    cacheWriteTokens: z.number(),
    durationMilliseconds: z.number(),
    estimatedTotalCostInUsd: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    toolCallCount: z.number(),
    toolErrorCount: z.number(),
    turnCount: z.number(),
});
export const EvaluationTaskReferenceSchema = z.object({
    taskIdentifier: z.string(),
    taskVersion: z.number().int().positive(),
});
export const EvaluationTrialProcessResultSchema = z.object({
    checks: z.array(EvaluationCheckResultSchema).optional(),
    recordedSession: z.object({
        error: z.string().optional(),
        metrics: EvaluationTrialMetricsSchema,
    }),
    status: z.enum(['executionFailure', 'failed', 'passed']),
    taskIdentifier: z.string(),
    taskVersion: z.number().int().positive(),
});
export const EvaluationTrialAttemptStatusSchema = z.enum([
    'executionFailure',
    'failed',
    'passed',
    'processFailure',
    'timedOut',
]);
export const EvaluationBatchConfigSchema = z.object({
    /** The task registry's declared checks; absent in artifacts recorded before declaration. */
    checkNames: EvaluationCheckNamesSchema.optional(),
    concurrency: z.number().int().min(1).max(16),
    infrastructureFailureRetryCount: z.number().int().min(0).max(10),
    model: AgentModelSchema,
    taskIdentifier: z.string(),
    taskVersion: z.number().int().positive(),
    thinkingLevel: AgentThinkingLevelSchema.optional(),
    timeoutMilliseconds: z.number().int().min(1).max(2_147_483_647),
    trialCount: z.number().int().min(1).max(10_000),
});
export const EvaluationBatchRunningArtifactSchema = z.object({
    batchId: z.string(),
    config: EvaluationBatchConfigSchema,
    startedAt: z.string(),
    status: z.literal('running'),
});
export const EvaluationTrialAttemptSchema = z.object({
    attemptNumber: z.number().int().positive(),
    checks: z.array(EvaluationCheckResultSchema).optional(),
    completedAt: z.string(),
    exitCode: z.number().int().nullable(),
    failureReason: z.string().optional(),
    metrics: EvaluationTrialMetricsSchema.optional(),
    processWallTimeMilliseconds: z.number().nonnegative(),
    resultPath: z.string().optional(),
    standardErrorPath: z.string(),
    standardOutputPath: z.string(),
    startedAt: z.string(),
    status: EvaluationTrialAttemptStatusSchema,
    terminationSignal: z.string().nullable(),
});
export const EvaluationBatchTrialSchema = z.object({
    attempts: z.array(EvaluationTrialAttemptSchema),
    /** Passed checks divided by declared checks; absent when the trial is not scored. */
    checkScore: z.number().min(0).max(1).optional(),
    finalStatus: EvaluationTrialAttemptStatusSchema,
    trialNumber: z.number().int().positive(),
});
export const EvaluationBatchStatusCountsSchema = z.object({
    executionFailure: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    processFailure: z.number().int().nonnegative(),
    timedOut: z.number().int().nonnegative(),
});
export const EvaluationBatchResultSchema = z.object({
    batchId: z.string(),
    completedAt: z.string(),
    config: EvaluationBatchConfigSchema,
    efficiency: EvaluationEfficiencyTotalsSchema.optional(),
    failureReason: z.string().optional(),
    score: EvaluationBatchScoreSchema.optional(),
    startedAt: z.string(),
    status: z.enum(['failed', 'incomplete', 'passed']),
    statusCounts: EvaluationBatchStatusCountsSchema,
    trials: z.array(EvaluationBatchTrialSchema),
});
export const EvaluationBatchArtifactSchema = z.union([
    EvaluationBatchRunningArtifactSchema,
    EvaluationBatchResultSchema,
]);
export const EvaluationUncommittedChangesSchema = z.enum(['allow', 'reject']);
export const EvaluationSuiteConfigSchema = EvaluationBatchConfigSchema.omit({
    checkNames: true,
    taskIdentifier: true,
    taskVersion: true,
}).extend({
    suiteIdentifier: z.string(),
    suiteType: z.enum(['capability', 'regression']),
    suiteVersion: z.number().int().positive(),
    tasks: z.array(EvaluationTaskReferenceSchema).min(1),
    uncommittedChanges: EvaluationUncommittedChangesSchema.optional(),
});
const GitObjectHashSchema = z.string().regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/u);
export const EvaluationSourceSnapshotSchema = z.object({
    capturedAt: z.string(),
    commitHash: GitObjectHashSchema,
    remoteUrl: z.string().optional(),
    /** With uncommitted changes, `treeHash` is the worktree content rather than `HEAD`'s tree. */
    treeHash: GitObjectHashSchema,
    worktreeState: z.enum(['clean', 'uncommittedChanges']).optional(),
});
export const EvaluationSourceSchema = z.discriminatedUnion('status', [
    z.object({
        completed: EvaluationSourceSnapshotSchema,
        started: EvaluationSourceSnapshotSchema,
        status: z.literal('completed'),
    }),
    z.object({
        completionFailureReason: z.string(),
        started: EvaluationSourceSnapshotSchema,
        status: z.literal('inspectionFailure'),
    }),
]);
export const EvaluationSourceCapsuleSchema = z.object({
    baseCommitHash: GitObjectHashSchema,
    checksum: z.string().regex(/^[0-9a-f]{64}$/u),
    commitHash: GitObjectHashSchema,
    format: z.literal('git-bundle-v1'),
    sizeBytes: z.number().int().positive(),
    treeHash: GitObjectHashSchema,
});
export const EvaluationSuiteRunningArtifactSchema = z.object({
    config: EvaluationSuiteConfigSchema,
    source: EvaluationSourceSnapshotSchema,
    startedAt: z.string(),
    status: z.literal('running'),
    suiteId: z.string(),
});
export const EvaluationSuiteTaskBatchSchema = z.object({
    batchId: z.string(),
    efficiency: EvaluationEfficiencyTotalsSchema.optional(),
    resultPath: z.string(),
    score: EvaluationBatchScoreSchema.optional(),
    status: z.enum(['failed', 'incomplete', 'passed']),
    statusCounts: EvaluationBatchStatusCountsSchema,
    taskIdentifier: z.string(),
    taskVersion: z.number().int().positive(),
});
export const EvaluationSuiteResultSchema = z.object({
    completedAt: z.string(),
    config: EvaluationSuiteConfigSchema,
    efficiency: EvaluationSuiteEfficiencySchema.optional(),
    failureReason: z.string().optional(),
    score: EvaluationSuiteScoreSchema.optional(),
    source: EvaluationSourceSchema,
    startedAt: z.string(),
    status: z.enum(['failed', 'incomplete', 'passed']),
    suiteId: z.string(),
    taskBatches: z.array(EvaluationSuiteTaskBatchSchema),
});
export const EvaluationSuiteArtifactSchema = z.union([
    EvaluationSuiteRunningArtifactSchema,
    EvaluationSuiteResultSchema,
]);
//# sourceMappingURL=evaluation.js.map